import Phaser from 'phaser';
import { setupCamera } from '../dpr.js';
import {
  RUNE_NODES, TREE_MAX_SPEND,
  MAX_TREE_POINTS, RUNE_TREE_RESET_COST,
  getNodeRank, canUpgradeNode, nodeReqsMet,
  totalSpentTokens, getTotalRanksSpent, getBranchRanksSpent,
  getPassiveBonuses, getRuneNode,
  type RuneNode, type RunePath, type ReqContext,
} from '@roe/shared';
import { GameStateManager } from '../state/GameStateManager';
import { drawEternalRuneStoneSymbol } from '../utils/glyphs';

// ── Colour palette by path ──
const PATH_COLORS: Record<RunePath, { node: number; dim: number; text: string; dimText: string }> = {
  war:     { node: 0xc03020, dim: 0x3a1010, text: '#e86050', dimText: '#5a2820' },
  arcane:  { node: 0x204878, dim: 0x0c1c2c, text: '#5090c0', dimText: '#1c3848' },
  fortune: { node: 0xb07820, dim: 0x3a2c10, text: '#e0b040', dimText: '#5a4020' },
  defense: { node: 0x406040, dim: 0x182018, text: '#80a860', dimText: '#2c3820' },
  linked:  { node: 0xc09020, dim: 0x3a2a08, text: '#e8c840', dimText: '#604810' },
};

const PATH_LABELS: Record<RunePath, string> = {
  war:     'ᚹ  War',
  arcane:  'ᚨ  Arcane',
  fortune: 'ᚠ  Fortune',
  defense: '',   // no column label — scattered cluster
  linked:  '',   // cross-path — no column label
};

// ── Node half-side by type (squares, v2 — compact 3-column grid) ──
// Invocation = 14 (smaller than keystone 18) so it reads as a "companion"
// node next to the elite rather than dominating it. Per user feedback the
// 22 px size was crowding the elite keystone label.
const NODE_R: Record<string, number> = { minor: 9, notable: 11, elite: 13, keystone: 18, invocation: 14 };

// ── v2 rune-icon override map ─────────────────────────────────────────────────
// Spec icons use unicode emoji glyphs (⚔ ❤ 🛡 🩸) which render as colored emoji
// on most systems. Replace with Elder Futhark runes so the tree reads as a
// monochrome runic UI. Each rune is chosen to vaguely fit the node's flavor.
const RUNE_ICONS: Record<string, string> = {
  // WRATH (offense)
  wrath_whetstone: 'ᚱ', wrath_honed: 'ᚲ',  wrath_tempo: 'ᚹ',  wrath_focus: 'ᚨ',  wrath_surge: 'ᛊ',
  wrath_tempered: 'ᚦ', wrath_fury: 'ᚺ',   wrath_burning: 'ᛞ', wrath_mastery: 'ᛗ', wrath_pen: 'ᚾ',
  wrath_bloodthirst: 'ᛒ', wrath_berserker: 'ᛁ', wrath_lethality: 'ᛇ',
  wrath_slayer: 'ᛏ', wrath_cleave: 'ᛢ', wrath_spell_crit: 'ᛜ',
  wrath_headsman: 'ᛣ', wrath_bloodfrenzy: 'ᛡ', wrath_twin_fang: 'ᛤ',
  wrath_lifesteal: 'ᛟ', wrath_spellbound: 'ᛠ', wrath_brutality: 'ᛥ', wrath_crimson_wound: 'ᛞ',
  wrath_warmonger: 'ᛏ',
  // WARD (defense)
  ward_vitality: 'ᛒ', ward_thickskin: 'ᛜ', ward_recovery: 'ᛇ', ward_bracing: 'ᛚ', ward_warding: 'ᛗ',
  ward_fortitude: 'ᚹ', ward_aegis: 'ᛉ', ward_hardened: 'ᚺ', ward_steady: 'ᛟ', ward_ironhide: 'ᚦ',
  ward_last_vow: 'ᛏ', ward_thornwall: 'ᛜ', ward_resolute: 'ᚾ', ward_unyielding: 'ᛜ', ward_purify: 'ᛇ', ward_evasion: 'ᛞ',
  ward_last_breath: 'ᛟ', ward_vow_of_iron: 'ᛏ', ward_revenant: 'ᛞ',
  ward_stalwart_stance: 'ᛗ', ward_eternal_hearth: 'ᚱ', ward_wardmaster: 'ᛉ', ward_hearthlight: 'ᛊ',
  ward_eternal_bastion: 'ᛇ',
  // CUNNING (utility)
  cun_fortune: 'ᚠ', cun_scholar_eye: 'ᛗ', cun_swift_mind: 'ᚱ', cun_scavenger: 'ᛡ', cun_recycler: 'ᛟ',
  cun_prosperity: 'ᚷ', cun_swift_steps: 'ᛞ', cun_quartermaster: 'ᛒ', cun_inquisitor: 'ᛇ', cun_runeforge: 'ᚲ',
  cun_rune_stride: 'ᚱ', cun_banished_tome: 'ᛈ', cun_tome_insight: 'ᛗ', cun_foresight: 'ᚨ',
  cun_pilgrims_tithe: 'ᛚ', cun_boon_hoarder: 'ᛟ',
  cun_wind_stride: 'ᚹ', cun_master_alchemist: 'ᛒ', cun_sigil_bond: 'ᛜ', cun_eternal_rune: 'ᛟ',
  cun_premonition: 'ᚨ', cun_pathseer: 'ᛇ', cun_scholar_pact: 'ᛏ',
  cun_warlords_crown: 'ᛟ',
};

// Rune decorations on left/right margin
const LEFT_RUNES  = ['ᚠ', 'ᚢ', 'ᚦ', 'ᚨ', 'ᚱ', 'ᚲ', 'ᚷ', 'ᚹ', 'ᚺ', 'ᚾ', 'ᛁ', 'ᛃ', 'ᛇ', 'ᛈ', 'ᛉ', 'ᛊ', 'ᛏ', 'ᛒ', 'ᛖ', 'ᛗ', 'ᛚ', 'ᛜ', 'ᛞ', 'ᛟ'];
const RIGHT_RUNES = ['ᛟ', 'ᛞ', 'ᛜ', 'ᛚ', 'ᛗ', 'ᛖ', 'ᛒ', 'ᛏ', 'ᛊ', 'ᛉ', 'ᛈ', 'ᛇ', 'ᛃ', 'ᛁ', 'ᚾ', 'ᚺ', 'ᚹ', 'ᚷ', 'ᚲ', 'ᚱ', 'ᚨ', 'ᚦ', 'ᚢ', 'ᚠ'];

const HEADER_H = 72;

export class PassiveTreeScene extends Phaser.Scene {
  private gsm!: GameStateManager;
  private fromScene: 'BattleScene' | 'MenuScene' = 'MenuScene';
  /** Reference to the launching BattleScene so closeTree() can resume the
   *  paused battle. Set in init() when launched from BattleScene; null when
   *  opened from MenuScene (full scene swap, nothing to resume). */
  private battleScene: (Phaser.Scene & { resumeBattle(): void }) | null = null;
  private scrollContainer!: Phaser.GameObjects.Container;
  private scrollY = 0;
  private maxScroll = 0;
  private isDragging = false;
  private dragStartY = 0;
  private dragScrollStart = 0;
  private tooltip!: Phaser.GameObjects.Container;
  private scrollBarThumb: Phaser.GameObjects.Rectangle | null = null;
  private tooltipNodeId: string | null = null;
  private tooltipHideTimer: Phaser.Time.TimerEvent | null = null;
  /** Radial hub center (computed in create()) */
  private hubX = 480;
  private hubY = 640;

  /** Cluster angle in radians for each path. War=left, Arcane=right, Fortune=bottom, Defense=top. */
  private sectorAngle(path: RunePath): number {
    switch (path) {
      case 'war':     return Math.PI;          // 180° — west
      case 'arcane':  return 0;                // 0° — east
      case 'fortune': return Math.PI * 0.5;    // 90° — south (screen-down)
      case 'defense': return Math.PI * 1.5;    // 270° — north (screen-up)
      case 'linked':  return Math.PI * 0.25;   // 45° — bridge (won't be used for sector label)
    }
  }

  private sectorLabel(path: RunePath): string {
    switch (path) {
      case 'war':     return 'ᚹ WAR';
      case 'arcane':  return 'ᚨ ARCANE';
      case 'fortune': return 'ᚠ FORTUNE';
      case 'defense': return '⛨ WARDS';
      case 'linked':  return '';
    }
  }

  /** Map the original y-tier to a radial distance from the hub. */
  private tierRadius(y: number): number {
    if (y < 100)   return 110;  // T1
    if (y < 220)   return 170;  // T2
    if (y < 340)   return 230;  // T3
    if (y < 460)   return 290;  // T4
    if (y < 580)   return 350;  // T5
    if (y < 700)   return 400;  // TS30
    if (y < 820)   return 440;  // T6 elite
    if (y < 940)   return 470;  // TCross bridges
    if (y < 1060)  return 500;  // TKey
    return 540;                 // outer — defense ward nodes
  }

  /** v2: 3-column tier-row layout. WRATH left, WARD center, CUNNING right.
   *  Tiers stack vertically (T1 → Elite). Replaces the v1 radial remap.
   *  Single-screen: fits in 960×504 below the 72px header without scrolling. */
  private remapNodesToWeb(): void {
    // Snapshot original spec y once so the tier bucket stays stable across repositions
    for (const n of RUNE_NODES) {
      if ((n as any)._origY === undefined) (n as any)._origY = n.y;
    }

    // v2 layout — compact 3-column grid that doesn't fill the whole canvas.
    const COL_X: Record<RunePath, number> = {
      war:     240,   // WRATH
      defense: 480,   // WARD
      fortune: 720,   // CUNNING
      arcane:  -9999, // unused in v2
      linked:  -9999, // unused in v2
    };
    const COL_SPAN = 160; // px range nodes spread across within a column (tighter)

    // Tier from spec y (set by passiveTree.ts: T1=140, T2=260, T3=400, T4=540, Elite=720)
    const tierOf = (y: number): number => {
      if (y < 200) return 0; // T1
      if (y < 320) return 1; // T2
      if (y < 470) return 2; // T3
      if (y < 620) return 3; // T4
      return 4;              // Elite
    };
    // Y position per tier (above HEADER_H=72; values in scrollContainer-local space).
    // The scrollContainer is offset by HEADER_H, so y=0 here is right under the header.
    const TIER_Y = [60, 140, 235, 340, 440];

    // Group nodes by branch (path)
    const byPath: Record<RunePath, RuneNode[]> = {
      war: [], defense: [], fortune: [], arcane: [], linked: [],
    };
    for (const n of RUNE_NODES) byPath[n.path].push(n);

    for (const path of (['war', 'defense', 'fortune'] as RunePath[])) {
      const branch = byPath[path];
      const cx = COL_X[path];
      // Group by tier
      const tiers: RuneNode[][] = [[], [], [], [], []];
      for (const n of branch) tiers[tierOf((n as any)._origY)].push(n);
      // Stable sort within tier by id so layout doesn't shuffle between rebuilds
      for (const t of tiers) t.sort((a, b) => a.id.localeCompare(b.id));

      for (let t = 0; t < tiers.length; t++) {
        const tier = tiers[t];
        const baseY = TIER_Y[t];
        if (tier.length === 0) continue;
        // Elite tier (t === 4) special-case: keystone CENTERED at `cx`,
        // invocation(s) parked to the RIGHT of the keystone on the same
        // row. Per user image feedback the previous "stack invocation
        // below keystone" layout was pushing the invocation icon off the
        // bottom of the canvas (label visible, icon clipped). Keeping
        // them on the same row at TIER_Y[4] = 440 (absolute 512) leaves
        // ~64 px of vertical padding to the canvas bottom — no clipping.
        if (t === 4) {
          const keystones = tier.filter((n) => n.type === 'keystone');
          const invocations = tier.filter((n) => n.type === 'invocation');
          const others = tier.filter((n) => n.type !== 'keystone' && n.type !== 'invocation');
          // Keystone(s) — centered at baseY. With one keystone per branch
          // (current spec) it lands exactly on cx.
          for (let i = 0; i < keystones.length; i++) {
            keystones[i].x = cx + (i - (keystones.length - 1) / 2) * 32;
            keystones[i].y = baseY;
          }
          // Invocation(s) — to the RIGHT of the keystone on the same row.
          // 80 px offset keeps both inside the 160 px column span.
          for (let j = 0; j < invocations.length; j++) {
            invocations[j].x = cx + 80 + j * 36;
            invocations[j].y = baseY;
          }
          // Anything else (defensive — empty in current spec) goes wherever
          // a single-row layout would put it.
          for (let k = 0; k < others.length; k++) {
            others[k].x = others.length === 1
              ? cx
              : Math.round(cx - COL_SPAN / 2 + (k * COL_SPAN) / (others.length - 1));
            others[k].y = baseY - 32;
          }
          continue;
        }
        if (tier.length <= 5) {
          // Single row
          for (let i = 0; i < tier.length; i++) {
            tier[i].x = tier.length === 1
              ? cx
              : Math.round(cx - COL_SPAN / 2 + (i * COL_SPAN) / (tier.length - 1));
            tier[i].y = baseY;
          }
        } else {
          // Split into 2 sub-rows so nodes don't crowd in a 240px wide column
          const top = Math.ceil(tier.length / 2);
          const bot = tier.length - top;
          const subRowGap = 50;
          for (let i = 0; i < top; i++) {
            tier[i].x = top === 1
              ? cx
              : Math.round(cx - COL_SPAN / 2 + (i * COL_SPAN) / (top - 1));
            tier[i].y = baseY - subRowGap / 2;
          }
          for (let j = 0; j < bot; j++) {
            tier[top + j].x = bot === 1
              ? cx
              : Math.round(cx - COL_SPAN / 2 + (j * COL_SPAN) / (bot - 1));
            tier[top + j].y = baseY + subRowGap / 2;
          }
        }
      }
    }
  }

  /** How many ranks the player has invested in nodes at the given ring index. */
  /** Shared mini-padlock graphic — matches the locked consumable slot style so the whole
   *  UI speaks the same "locked" visual language. Scale adjusts base size (1.0 = consumable-bar). */
  private drawMiniLock(x: number, y: number, scale = 1, depth = 15): Phaser.GameObjects.Graphics {
    const g = this.add.graphics().setDepth(depth);
    const s = scale;
    // Shackle (arch)
    g.lineStyle(Math.max(1, 2 * s), 0x3a3830, 0.85);
    g.beginPath();
    g.moveTo(x - 4 * s, y - 1 * s);
    g.lineTo(x - 4 * s, y - 5 * s);
    g.arc(x, y - 5 * s, 4 * s, Math.PI, 0, false);
    g.lineTo(x + 4 * s, y - 1 * s);
    g.strokePath();
    // Body
    g.fillStyle(0x252220, 0.9);
    g.fillRect(x - 6 * s, y - 1 * s, 12 * s, 8 * s);
    g.lineStyle(1, 0x3a3530, 0.7);
    g.strokeRect(x - 6 * s, y - 1 * s, 12 * s, 8 * s);
    // Keyhole
    g.fillStyle(0x070504, 1);
    g.fillCircle(x, y + 2 * s, 1.5 * s);
    g.fillRect(x - 1 * s, y + 2 * s, 2 * s, 3 * s);
    return g;
  }

  private ringInvestment(ringIdx: number): number {
    const tree = this.gsm.player.passiveTree ?? {};
    const ringOf = (node: RuneNode): number => {
      // Same ring buckets used for positioning — but based on the node's ORIGINAL y
      // before repositioning, we stored that under _origY. Fallback: use position.
      const origY = (node as any)._origY ?? node.y;
      if (origY < 220)  return 0;
      if (origY < 460)  return 1;
      if (origY < 700)  return 2;
      if (origY < 940)  return 3;
      return 4;
    };
    let sum = 0;
    for (const n of RUNE_NODES) {
      if (ringOf(n) === ringIdx) sum += tree[n.id] ?? 0;
    }
    return sum;
  }

  /** Stable hash for a string — used to produce deterministic per-node jitter. */
  private hashString(s: string): number {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
    return h >>> 0;
  }

  constructor() { super({ key: 'PassiveTreeScene' }); }

  init(data?: {
    gsm?: GameStateManager;
    scrollY?: number;
    from?: 'BattleScene' | 'MenuScene';
    battleScene?: Phaser.Scene & { resumeBattle(): void };
  }): void {
    this.gsm = data?.gsm ?? GameStateManager.getInstance();
    this.scrollY = data?.scrollY ?? 0;
    this.fromScene = data?.from ?? 'MenuScene';
    this.battleScene = data?.battleScene ?? null;
  }

  /** Close the tree: if we were launched as an overlay on BattleScene, stop
   *  ourselves AND resume BattleScene (which was paused on tree-open per the
   *  no-mid-fight-exploits guard in BattleScene.launchOverlay). When opened
   *  from MenuScene, go back to the menu via a full scene swap. */
  private closeTree(): void {
    if (this.fromScene === 'BattleScene') {
      this.scene.stop();
      this.battleScene?.resumeBattle();
    } else {
      this.scene.start('MenuScene', { gsm: this.gsm });
    }
  }

  /** Build context for requirement checks */
  private getReqContext(): ReqContext {
    return {
      highestLevel: this.gsm.getAccountMaxHighestLevel(),
      completedAchievementIds: this.gsm.account.achievements
        .filter((a) => a.completed || a.claimed)
        .map((a) => a.id),
    };
  }

  create(): void {
    setupCamera(this, this.cameras.main, 'full');
    const W = 960;
    const H = 576;
    this.cameras.main.setBackgroundColor('#060402');

    const SCROLL_H = H - HEADER_H;
    this.maxScroll = 0;            // v2 — single-screen, no scroll
    this.isDragging = false;
    this.tooltipNodeId = null;

    // ── Fixed header ──
    this.add.rectangle(W / 2, HEADER_H / 2, W, HEADER_H, 0x080402, 1).setDepth(20);
    this.add.rectangle(W / 2, HEADER_H, W, 1, 0x2a1a08, 0.9).setDepth(20);

    this.add.text(W / 2, 18, 'ᚱ  Ancestral Rune Tree  ᚱ', {
      fontSize: '20px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(21);

    this.createHeader(W);

    // ── Radial / web remap: rewrite every node's (x, y) into a circular layout ──
    // Center hub is at (CX, CY). Each path occupies a 90° sector; nodes are placed
    // at a radius based on their tier and spread laterally across the sector arc.
    this.remapNodesToWeb();

    // ── Scrollable content ──
    // Per user feedback: branch titles (WRATH / WARD / CUNNING) need a real
    // gap from the top header. Bumped 20 → 48 so the labels breathe well
    // clear of the menu bar and don't read as crowding the page header.
    const SCROLL_TOP_PAD = 48;
    this.scrollContainer = this.add.container(0, HEADER_H + SCROLL_TOP_PAD);
    this.drawMarginRunes(W);

    // ── v2 layout: 3 column branches with tier rows ──
    // Branch labels at the top of each column.
    const COL_X: Record<RunePath, number> = {
      war:     240, defense: 480, fortune: 720,
      arcane:  -9999, linked: -9999,
    };
    const branches: Array<{ path: RunePath; name: string; icon: string }> = [
      { path: 'war',     name: 'WRATH',   icon: 'ᚹ' },
      { path: 'defense', name: 'WARD',    icon: '⛨' },
      { path: 'fortune', name: 'CUNNING', icon: 'ᚠ' },
    ];
    for (const b of branches) {
      const col = PATH_COLORS[b.path];
      const cx = COL_X[b.path];
      const branchRanks = getBranchRanksSpent(this.gsm.player.passiveTree ?? {}, b.path);
      // Branch header (icon + name) — bumped y=10 → y=24 per user feedback
      // so the title gets a real gap from the header hairline at absolute
      // y=72 (scrollContainer is anchored at HEADER_H, so title at local
      // y=24 → absolute y=96, ~24 px clearance under the line).
      this.scrollContainer.add(
        this.add.text(cx, 24, `${b.icon}  ${b.name}`, {
          fontSize: '15px', fontFamily: "'Cinzel Decorative', 'Palatino Linotype', Georgia, serif",
          color: col.text, fontStyle: 'bold', stroke: '#0a0402', strokeThickness: 3,
        }).setOrigin(0.5).setDepth(11).setAlpha(0.95),
      );
      // Per-branch rank counter — pushed down with the title.
      this.scrollContainer.add(
        this.add.text(cx, 42, `${branchRanks} ranks`, {
          fontSize: '9px', fontFamily: 'Georgia, serif',
          color: branchRanks > 0 ? '#a07840' : '#5a4020',
        }).setOrigin(0.5).setDepth(11).setAlpha(0.85),
      );
    }

    // Column dividers — vertical lines between branches.
    {
      const dividers = this.add.graphics().setDepth(0);
      dividers.lineStyle(1, 0x3a2810, 0.55);
      const div1X = (240 + 480) / 2;   // 360 (between WRATH and WARD)
      const div2X = (480 + 720) / 2;   // 600 (between WARD and CUNNING)
      dividers.beginPath();
      dividers.moveTo(div1X, 50);  dividers.lineTo(div1X, 460);
      dividers.moveTo(div2X, 50);  dividers.lineTo(div2X, 460);
      dividers.strokePath();
      this.scrollContainer.add(dividers);
    }

    // Tier labels on the right margin only — horizontal lines were cutting
    // across nodes between tiers and reading as visual noise.
    const TIER_Y = [60, 140, 235, 340, 440];
    const TIER_NAMES = ['T1', 'T2', 'T3', 'T4', 'ELITE'];
    for (let i = 0; i < TIER_Y.length; i++) {
      this.scrollContainer.add(
        this.add.text(W - 12, TIER_Y[i], TIER_NAMES[i], {
          fontSize: '8px', fontFamily: 'Georgia, serif', color: '#5a4020', fontStyle: 'bold',
        }).setOrigin(0.5).setDepth(1).setAlpha(0.5),
      );
    }

    // Prereq lines — every node that lists `requires` gets a connection drawn
    // to its parent. Keystones use an L-shaped route so the line never cuts
    // through other T4 nodes (drops down, jogs sideways, drops to keystone).
    // Regular nodes use a straight line — they're typically on adjacent
    // tiers so no jog is needed.
    {
      const conn = this.add.graphics().setDepth(1);
      const tree = this.gsm.player.passiveTree ?? {};
      for (const node of RUNE_NODES) {
        for (const reqId of node.requires) {
          const req = RUNE_NODES.find((n) => n.id === reqId);
          if (!req) continue;
          const reqRank = tree[reqId] ?? 0;
          const reqUnlocked = reqRank >= 1;
          const reqMaxed = reqRank >= req.maxRank;
          // Bright + opaque when fully maxed; dim + faded when not yet
          // unlocked; mid-tone when partially unlocked.
          const colour = reqMaxed ? 0xc8973a : reqUnlocked ? 0x9a7228 : 0x6a4818;
          const alpha = reqMaxed ? 0.85 : reqUnlocked ? 0.65 : 0.45;
          conn.lineStyle(2, colour, alpha);
          conn.beginPath();
          if (node.type === 'keystone') {
            const jogY = (req.y + node.y) / 2;
            conn.moveTo(req.x, req.y);
            conn.lineTo(req.x, jogY);
            conn.lineTo(node.x, jogY);
            conn.lineTo(node.x, node.y);
          } else {
            conn.moveTo(req.x, req.y);
            conn.lineTo(node.x, node.y);
          }
          conn.strokePath();
        }
      }
      this.scrollContainer.add(conn);
    }

    for (const node of RUNE_NODES) this.drawNode(node);

    // Clip scrollable area
    const mask = this.make.graphics({});
    mask.fillRect(0, HEADER_H - 2, W, SCROLL_H + 4);
    this.scrollContainer.setMask(mask.createGeometryMask());

    // Scrollbar removed — navigation is wheel-zoom + drag-pan

    this.tooltip = this.add.container(0, 0).setDepth(50).setVisible(false);

    // v2: single-screen layout. No zoom, no pan — the entire 3-column tree fits in
    // 960×504 below the 72px header. Container is anchored at (0, HEADER_H).
    this.scrollContainer.setScale(1);
    this.scrollContainer.x = 0;
    this.scrollContainer.y = HEADER_H;
    this.maxScroll = 0;

    // ── ESC → back to menu ──
    const canvas = this.game.canvas;
    canvas.setAttribute('tabindex', '1');
    canvas.focus();
    if (this.input.keyboard) {
      this.input.keyboard.once('keydown-ESC', () => this.closeTree());
    }
    this.input.on('pointerdown', () => canvas.focus());

    // ── Tooltip detection via pointermove ──
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (this.isDragging) return;
      // Convert pointer (canvas pixels) → scene coords via camera, so hover stays accurate after HD upscale.
      const worldPt = this.cameras.main.getWorldPoint(p.x, p.y);
      const sx = worldPt.x;
      const sy = worldPt.y;
      if (sy < HEADER_H) {
        if (this.tooltipNodeId !== null && !this.tooltipHideTimer) {
          this.tooltipHideTimer = this.time.delayedCall(120, () => {
            this.tooltip.setVisible(false);
            this.tooltipNodeId = null;
            this.tooltipHideTimer = null;
          });
        }
        return;
      }
      // Scene → container-local via the tree's zoom + pan transform
      const localX = (sx - this.scrollContainer.x) / this.treeZoom;
      const localY = (sy - this.scrollContainer.y) / this.treeZoom;
      // Generous slack in screen space so nodes are easy to hover at any zoom level.
      // Pick the CLOSEST node to the pointer instead of first-hit so overlaps don't steal priority.
      const slack = 22 / Math.max(this.treeZoom, 0.4);
      let hoveredNode: RuneNode | null = null;
      let bestDistSq = Infinity;
      for (const node of RUNE_NODES) {
        const r = (NODE_R[node.type] ?? 20) + slack;
        const dx = localX - node.x;
        const dy = localY - node.y;
        const d2 = dx * dx + dy * dy;
        if (d2 <= r * r && d2 < bestDistSq) {
          bestDistSq = d2;
          hoveredNode = node;
        }
      }
      if (hoveredNode) {
        if (this.tooltipHideTimer) { this.tooltipHideTimer.remove(false); this.tooltipHideTimer = null; }
        if (this.tooltipNodeId !== hoveredNode.id) this.showTooltip(hoveredNode);
      } else if (this.tooltipNodeId !== null && !this.tooltipHideTimer) {
        this.tooltipHideTimer = this.time.delayedCall(120, () => {
          this.tooltip.setVisible(false);
          this.tooltipNodeId = null;
          this.tooltipHideTimer = null;
        });
      }
    });

    // v2: zoom + drag-pan are disabled. The 3-column tree fits on a single screen,
    // so the player just hovers nodes to read tooltips and clicks to upgrade.
  }

  // ── Header: rune count, point budget, reset, close ──
  private createHeader(W: number): void {
    const tokens = this.gsm.player.resources.loot;
    const tree = this.gsm.player.passiveTree ?? {};
    const ranksSpent = getTotalRanksSpent(tree);
    const runesSpent = totalSpentTokens(tree);
    const atBudget = ranksSpent >= MAX_TREE_POINTS;
    const canReset = runesSpent > 0;
    const canAffordReset = tokens >= RUNE_TREE_RESET_COST;

    // Record stage sits to the LEFT of the runes count on the same baseline.
    const RUNES_X = W / 2 - 150;
    const STATS_Y = 46;
    this.add.text(RUNES_X, STATS_Y, `◆ ${tokens} runes`, {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#a07840',
      stroke: '#2a1004', strokeThickness: 3,
    }).setOrigin(0, 0.5).setDepth(21);

    // Record stage — highest stage reached this account (used for zone-gated talents)
    const recordStage = this.gsm.getAccountMaxHighestLevel();
    this.add.text(RUNES_X - 24, STATS_Y, `⚔ Record Stage: ${recordStage}`, {
      fontSize: '10px', fontFamily: "'Cinzel Decorative', 'Palatino Linotype', Georgia, serif",
      color: '#d4a84a', fontStyle: 'bold', stroke: '#2a1004', strokeThickness: 2,
    }).setResolution(2).setOrigin(1, 0.5).setDepth(21);

    // Point budget pip — central text shows X / 90. Goes red when full.
    const budgetColor = atBudget ? '#c05030' : ranksSpent >= MAX_TREE_POINTS - 5 ? '#d4a84a' : '#7a6030';
    this.add.text(W / 2, 46, `${ranksSpent} / ${MAX_TREE_POINTS} points`, {
      fontSize: '11px', fontFamily: "'Cinzel Decorative', 'Palatino Linotype', Georgia, serif",
      color: budgetColor, fontStyle: 'bold', stroke: '#2a1004', strokeThickness: 2,
    }).setOrigin(0.5).setDepth(21);

    // Reset button — costs 120 runes, refunds every rank's rune cost.
    const resetBg = this.add.rectangle(W / 2 + 180, 46, 170, 22, 0x0a0602, 1)
      .setStrokeStyle(1, canReset && canAffordReset ? 0x805010 : 0x2a1808).setDepth(21);
    const resetColor = canReset && canAffordReset ? '#c09030' : '#3a2010';
    // Beta build: RUNE_TREE_RESET_COST is 0, so the cost prefix is dropped
    // entirely and the button just reads "Reset (free) — refunds N◆". When
    // a real cost lands again the original "-X◆ → +Y◆" template kicks back
    // in (the conditional below keeps both paths working).
    const resetLabel = !canReset
      ? 'Reset tree'
      : RUNE_TREE_RESET_COST > 0
        ? `Reset (-${RUNE_TREE_RESET_COST}◆ → +${runesSpent}◆)`
        : `Reset (free) — refunds ${runesSpent}◆`;
    const resetTxt = this.add.text(W / 2 + 180, 46, resetLabel, {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: resetColor,
    }).setOrigin(0.5).setDepth(22);
    if (canReset && canAffordReset) {
      resetBg.setInteractive({ useHandCursor: true });
      resetTxt.setInteractive({ useHandCursor: true });
      const doReset = () => {
        // Pay the reset cost, refund every rune previously spent.
        this.gsm.player.resources.loot = this.gsm.player.resources.loot - RUNE_TREE_RESET_COST + runesSpent;
        this.gsm.player.passiveTree = {};
        this.gsm.save();
        // Preserve `from` + `battleScene` across the restart so closeTree()
        // still resumes the paused BattleScene when the player closes the
        // tree after a reset (otherwise the restart wipes those fields back
        // to MenuScene defaults and the battle stays paused / orphaned).
        this.scene.restart({ gsm: this.gsm, scrollY: 0, from: this.fromScene, battleScene: this.battleScene ?? undefined });
      };
      resetBg.on('pointerdown', doReset);
      resetTxt.on('pointerdown', doReset);
      resetBg.on('pointerover', () => { resetTxt.setColor('#e0b040'); resetBg.setStrokeStyle(1, 0x9a6020); });
      resetBg.on('pointerout', () => { resetTxt.setColor('#c09030'); resetBg.setStrokeStyle(1, 0x805010); });
    }

    const closeBtn = this.add.text(W - 24, 18, '✕', {
      fontSize: '16px', fontFamily: 'Georgia, serif', color: '#5a3600',
    }).setOrigin(0.5).setDepth(22).setInteractive({ useHandCursor: true });
    closeBtn.on('pointerover', () => closeBtn.setColor('#c8973a'));
    closeBtn.on('pointerout', () => closeBtn.setColor('#5a3600'));
    closeBtn.on('pointerdown', () => this.closeTree());
  }

  private drawMarginRunes(W: number): void {
    const spacing = 56;
    for (let i = 0; i < LEFT_RUNES.length; i++) {
      this.scrollContainer.add(
        this.add.text(32, 20 + i * spacing, LEFT_RUNES[i], {
          fontSize: '28px', fontFamily: 'Georgia, serif', color: '#1a0808',
        }).setOrigin(0.5).setDepth(5),
      );
      this.scrollContainer.add(
        this.add.text(W - 32, 20 + i * spacing, RIGHT_RUNES[i], {
          fontSize: '28px', fontFamily: 'Georgia, serif', color: '#1a0808',
        }).setOrigin(0.5).setDepth(5),
      );
    }
  }

  private drawConnections(): void {
    const tree = this.gsm.player.passiveTree ?? {};
    for (const node of RUNE_NODES) {
      for (const reqId of node.requires) {
        const parent = RUNE_NODES.find((n) => n.id === reqId);
        if (!parent) continue;
        const parentNode = getRuneNode(reqId);
        const unlocked = parentNode
          ? getNodeRank(tree, reqId) >= parentNode.maxRank
          : getNodeRank(tree, reqId) >= 1;
        const linePathColor = node.path === 'linked' || node.path === 'defense'
          ? PATH_COLORS[node.path]
          : PATH_COLORS[node.path];
        const line = this.add.graphics().setDepth(8);
        line.lineStyle(2, unlocked ? linePathColor.node : linePathColor.dim, unlocked ? 0.7 : 0.22);
        line.beginPath();
        line.moveTo(parent.x, parent.y);
        line.lineTo(node.x, node.y);
        line.strokePath();
        this.scrollContainer.add(line);
      }
    }
  }

  private drawNode(node: RuneNode): void {
    const tree = this.gsm.player.passiveTree ?? {};
    const ctx = this.getReqContext();
    const rank = getNodeRank(tree, node.id);
    const col = PATH_COLORS[node.path];
    const r = NODE_R[node.type] ?? 20;
    const isMaxed = rank >= node.maxRank;
    const reqsMet = nodeReqsMet(tree, node, ctx);
    const affordable = this.gsm.player.resources.loot >= node.costPerRank;
    const ranksSpent = getTotalRanksSpent(tree);
    const atBudget = ranksSpent >= MAX_TREE_POINTS;
    // v2 — branch-rank gate is enforced inside nodeReqsMet, no separate ring check needed.
    const ringUnlocked = true;
    const canLevel = !isMaxed && reqsMet && affordable && !atBudget;
    const isStandalone = (node.requiresZone !== undefined || node.requiresAchievement !== undefined)
      && node.requires.length === 0;

    // ── Keystone: prominent diamond border (always visible) + glow when unlocked ──
    if (node.type === 'keystone') {
      const diagSize = r * 1.7;
      const diamond = this.add.rectangle(node.x, node.y, diagSize, diagSize, 0, 0)
        .setRotation(Math.PI / 4)
        .setStrokeStyle(2.5, rank > 0 ? col.node : col.dim, rank > 0 ? 1 : 0.5)
        .setDepth(13);
      this.scrollContainer.add(diamond);
      const diagOuter = r * 2.1;
      const diamondOuter = this.add.rectangle(node.x, node.y, diagOuter, diagOuter, 0, 0)
        .setRotation(Math.PI / 4)
        .setStrokeStyle(1, col.node, rank > 0 ? 0.45 : 0.18)
        .setDepth(9);
      this.scrollContainer.add(diamondOuter);
      if (canLevel) {
        this.tweens.add({ targets: [diamond, diamondOuter], alpha: { from: 0.4, to: 1 }, duration: 1200, yoyo: true, repeat: -1 });
      }
      if (rank > 0) {
        const glow = this.add.circle(node.x, node.y, r + 12, col.node, 0.12).setDepth(9);
        this.scrollContainer.add(glow);
      }
    }

    // ── Notable: subtle outer ring ──
    if (node.type === 'notable') {
      const ring = this.add.circle(node.x, node.y, r + 5, col.node, 0)
        .setStrokeStyle(1, col.node, rank > 0 ? 0.45 : 0.15).setDepth(9);
      this.scrollContainer.add(ring);
    }

    // ── Invocation: subtle outer ring in the cast-bar accent (red / gold /
    // blue per branch). Per user feedback the previous big halo + glow was
    // dominating the elite keystone next to it. Now reduced to one thin
    // outer ring so the invocation reads as a small companion node. ──
    if (node.type === 'invocation') {
      const INVOC_ACCENTS: Record<string, number> = {
        inv_avenging_wrath: 0xef4444, // red — WRATH
        inv_aegis_of_light: 0xfde68a, // gold — WARD
        inv_time_pull:      0x8b9cf0, // blue — CUNNING
      };
      const accent = INVOC_ACCENTS[node.id] ?? col.node;
      const ringSize = (r + 5) * 2;
      const ring = this.add.rectangle(node.x, node.y, ringSize, ringSize, 0, 0)
        .setStrokeStyle(1.5, accent, rank > 0 ? 0.7 : reqsMet ? 0.45 : 0.18).setDepth(10);
      this.scrollContainer.add(ring);
      if (canLevel) {
        this.tweens.add({ targets: ring, alpha: { from: 0.4, to: 1 }, duration: 1100, yoyo: true, repeat: -1 });
      }
    }

    // ── Cross-path extra concentric ring ──
    if (node.path === 'linked') {
      const ring2 = this.add.circle(node.x, node.y, r + 7, col.node, 0)
        .setStrokeStyle(1, col.node, rank > 0 ? 0.5 : 0.18).setDepth(9);
      this.scrollContainer.add(ring2);
    }

    // ── Requirement-type border color:
    //     red=ring-gate locked, purple=achievement, amber=zone, gold=parent-met
    let reqBorderColor = col.node;
    if (!ringUnlocked) reqBorderColor = 0x8b2020;                        // ring-gate → dark red
    else if (node.requiresAchievement) reqBorderColor = 0xc060ff;        // achievement → purple
    else if (node.requiresZone !== undefined) reqBorderColor = 0xe8a040; // zone → amber
    else if (node.requires.length > 0) reqBorderColor = 0xd4a84a;        // parent → gold

    // ── Node base fill ──
    // Invocations override the path-derived color with their dedicated accent
    // (red / gold / blue) — same swatch as the cast-bar HUD button so the tree
    // node and the castable button read as visually identical.
    let fill: number;
    let strokeColor: number;
    let strokeAlpha: number;
    if (node.type === 'invocation') {
      const INVOC_ACCENTS: Record<string, number> = {
        inv_avenging_wrath: 0xef4444,
        inv_aegis_of_light: 0xfde68a,
        inv_time_pull:      0x8b9cf0,
      };
      const invAccent = INVOC_ACCENTS[node.id] ?? col.node;
      fill = rank > 0 ? invAccent : Phaser.Display.Color.IntegerToColor(invAccent).darken(60).color;
      strokeColor = invAccent;
      strokeAlpha = canLevel || rank > 0 ? 1 : reqsMet ? 0.75 : 0.4;
    } else {
      fill = rank > 0 ? (isMaxed ? col.node : Phaser.Display.Color.IntegerToColor(col.node).darken(20).color) : col.dim;
      strokeColor = canLevel ? reqBorderColor : (rank > 0 ? col.node : reqsMet ? reqBorderColor : col.dim);
      strokeAlpha = canLevel ? 1 : (rank > 0 ? 0.9 : reqsMet ? 0.7 : 0.3);
    }
    // v2: square nodes (instead of circles) for a runestone-grid feel.
    // Outer textured border + inner main square + inner highlight stripe.
    const outerSize = (r + 3) * 2;
    const mainSize = r * 2;
    const innerSize = (r - 3) * 2;
    const outerRing = this.add.rectangle(node.x, node.y, outerSize, outerSize, 0, 0)
      .setStrokeStyle(1, strokeColor, strokeAlpha * 0.5).setDepth(11);
    this.scrollContainer.add(outerRing);
    const circle = this.add.rectangle(node.x, node.y, mainSize, mainSize, fill, rank > 0 ? 0.92 : 0.5)
      .setStrokeStyle(canLevel ? 3 : 2, strokeColor, strokeAlpha)
      .setDepth(12).setInteractive({ useHandCursor: !isMaxed && reqsMet && !atBudget });
    this.scrollContainer.add(circle);
    // Inner highlight outline (subtle gold) for worn-stone feel
    if (innerSize > 4) {
      const innerRing = this.add.rectangle(node.x, node.y, innerSize, innerSize, 0, 0)
        .setStrokeStyle(1, 0xd4a84a, rank > 0 ? 0.25 : 0.1).setDepth(12.5);
      this.scrollContainer.add(innerRing);
    }

    // ── ELITE: diamond overlay border ──
    if (node.type === 'elite') {
      const diagSize = r * 1.55;
      // Inner diamond outline
      const diamond = this.add.rectangle(node.x, node.y, diagSize, diagSize, 0, 0)
        .setRotation(Math.PI / 4)
        .setStrokeStyle(2, rank > 0 ? col.node : col.dim, rank > 0 ? 0.9 : 0.35)
        .setDepth(13);
      this.scrollContainer.add(diamond);
      // Outer diamond glow
      const diagOuter = r * 1.85;
      const diamondOuter = this.add.rectangle(node.x, node.y, diagOuter, diagOuter, 0, 0)
        .setRotation(Math.PI / 4)
        .setStrokeStyle(1, col.node, rank > 0 ? 0.35 : 0.12)
        .setDepth(9);
      this.scrollContainer.add(diamondOuter);
      // Animate outer diamond when can level
      if (canLevel) {
        this.tweens.add({ targets: diamondOuter, alpha: { from: 0.2, to: 0.7 }, duration: 1100, yoyo: true, repeat: -1 });
      }
    }

    // ── Standalone node: amber border (squared, matches the v2 runestone grid) ──
    if (isStandalone && !reqsMet) {
      const lockSize = (r + 9) * 2;
      const lockRing = this.add.rectangle(node.x, node.y, lockSize, lockSize, 0, 0)
        .setStrokeStyle(2, 0xc09020, 0.55).setDepth(14);
      this.scrollContainer.add(lockRing);
      const badgeX = node.x + Math.round(r * 0.72);
      const badgeY = node.y - Math.round(r * 0.72);
      const badge = this.add.circle(badgeX, badgeY, 6, 0x3a2010, 0.95).setDepth(15);
      this.scrollContainer.add(badge);
      this.scrollContainer.add(this.drawMiniLock(badgeX, badgeY, 0.75, 16));
    } else if (isStandalone && reqsMet) {
      const glowSize = (r + 7) * 2;
      const glowRing = this.add.rectangle(node.x, node.y, glowSize, glowSize, 0, 0)
        .setStrokeStyle(2, 0xd4a010, 0.65).setDepth(14);
      this.scrollContainer.add(glowRing);
      this.tweens.add({ targets: glowRing, alpha: { from: 0.3, to: 0.9 }, duration: 1200, yoyo: true, repeat: -1 });
    }

    // ── Icon — centered in node. Eternal Rune Stone gets the same triquetra
    // drawn on the HUD button so the tree + HUD share one visual language.
    // Everything else stays on the text-rune pipeline.
    const iconAlpha = rank > 0 ? 1 : (reqsMet ? 0.65 : 0.25);
    if (node.id === 'rune_deep_checkpoint') {
      const gfx = this.add.graphics().setDepth(13).setAlpha(iconAlpha);
      const symSize = node.type === 'elite' ? 18 : 14;
      const triTint = rank > 0 ? 0xff7050 : reqsMet ? 0xe04030 : 0x6b2010;
      drawEternalRuneStoneSymbol(gfx, node.x, node.y, symSize, triTint);
      this.scrollContainer.add(gfx);
    } else {
      const iconFontSize = node.type === 'keystone' ? '18px' : node.type === 'elite' ? '15px' : node.type === 'notable' ? '13px' : '12px';
      // v2: prefer the rune override (always Elder Futhark glyph) over the
      // spec icon, which often resolves as a colored emoji glyph on Windows.
      const runeChar = RUNE_ICONS[node.id] ?? node.icon;
      const icon = this.add.text(node.x, node.y, runeChar, {
        fontSize: iconFontSize,
        fontFamily: "'Cinzel Decorative', 'Palatino Linotype', Georgia, serif", fontStyle: 'bold',
        color: rank > 0 ? '#f0d080' : (reqsMet ? '#d4a84a' : '#6b4020'),
        stroke: '#0a0402', strokeThickness: 3,
      }).setOrigin(0.5, 0.5).setDepth(13).setAlpha(iconAlpha);
      this.scrollContainer.add(icon);
    }

    // ── Rank text — only shown when rank > 0 (avoids "0/N" clutter
    //    overlapping between adjacent nodes in the dense 3-column grid).
    //    Positioned BELOW the node so it never collides with neighbours.
    if (rank > 0) {
      const rankStr = `${rank}/${node.maxRank}`;
      const rankY = node.y + r + 7;
      const rankColor = col.text;
      const rankTxt = this.add.text(node.x, rankY, rankStr, {
        fontSize: '9px', fontFamily: "'Consolas', 'Courier New', monospace", color: rankColor, fontStyle: 'bold',
        stroke: '#0a0402', strokeThickness: 2,
      }).setOrigin(0.5, 0).setDepth(14);
      this.scrollContainer.add(rankTxt);
    }

    // v2: always-on labels were colliding in the dense 3-column tier rows.
    // Only the per-branch keystone (capstone) shows its label on the canvas
    // — every other node surfaces its name via the hover tooltip instead.
    if (node.type === 'keystone') {
      // When rank > 0 the rank number ("1/1") renders at node.y+r+7 and was
      // colliding with the keystone label at node.y+r+11. Push the label
      // below the rank stamp (extra +14 px) so both fit cleanly per user
      // feedback. Untaken keystones still anchor the label tight to the node.
      const labelY = rank > 0 ? node.y + r + 25 : node.y + r + 11;
      const labelColor = rank > 0 ? col.text : (reqsMet ? '#a88860' : '#6b503a');
      const label = this.add.text(node.x, labelY, node.label, {
        fontSize: '10px', fontFamily: "'Cinzel Decorative', 'Palatino Linotype', Georgia, serif",
        color: labelColor, fontStyle: 'bold', stroke: '#0a0402', strokeThickness: 3,
      }).setOrigin(0.5, 0).setDepth(13);
      this.scrollContainer.add(label);
    }

    // ── Affordability pulse (squared, not circle) ──
    if (canLevel && node.type !== 'elite' && node.type !== 'keystone') {
      const pulseSize = (r + 4) * 2;
      const ring = this.add.rectangle(node.x, node.y, pulseSize, pulseSize, col.node, 0)
        .setStrokeStyle(1.5, col.node, 0.5).setDepth(11);
      this.scrollContainer.add(ring);
      this.tweens.add({ targets: ring, alpha: { from: 0.3, to: 0.8 }, duration: 900, yoyo: true, repeat: -1 });
    }

    // ── Spend cap indicator ──
    if (atBudget && !isMaxed && rank === 0) {
      const capDot = this.add.circle(node.x - Math.round(r * 0.72), node.y - Math.round(r * 0.72), 4, 0x6a3020, 0.8)
        .setDepth(14);
      this.scrollContainer.add(capDot);
    }

    // ── Click handler ──
    circle.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      pointer.event.stopPropagation();
      if (this.tooltipHideTimer) { this.tooltipHideTimer.remove(false); this.tooltipHideTimer = null; }
      if (this.isDragging) return;
      this.showTooltip(node);
      if (!canLevel) return;
      // v2 point-budget check (90 ranks max across the whole tree)
      const currentRanks = getTotalRanksSpent(this.gsm.player.passiveTree ?? {});
      if (currentRanks >= MAX_TREE_POINTS) return;
      if (!this.gsm.player.passiveTree) this.gsm.player.passiveTree = {};
      this.gsm.player.passiveTree[node.id] = (this.gsm.player.passiveTree[node.id] ?? 0) + 1;
      this.gsm.player.resources.loot -= node.costPerRank;
      this.gsm.save();
      // Preserve fromScene + battleScene across the restart so closeTree
      // still resumes the paused battle when the player exits the tree.
      this.scene.restart({ gsm: this.gsm, scrollY: this.scrollY, from: this.fromScene, battleScene: this.battleScene ?? undefined });
    });
  }

  // ── Tooltip ──
  private showTooltip(node: RuneNode): void {
    const tree = this.gsm.player.passiveTree ?? {};
    const ctx = this.getReqContext();
    const rank = getNodeRank(tree, node.id);
    const col = PATH_COLORS[node.path];
    const isMaxed = rank >= node.maxRank;
    const reqsMet = nodeReqsMet(tree, node, ctx);
    const ranksSpent = getTotalRanksSpent(tree);
    const atBudget = ranksSpent >= MAX_TREE_POINTS;

    const prev = [...this.tooltip.list] as Phaser.GameObjects.GameObject[];
    prev.forEach((c) => c.destroy());
    this.tooltip.removeAll(false);
    this.tooltipNodeId = node.id;

    const W = 240;
    const lines: { text: string; color: string; size?: string }[] = [];

    lines.push({ text: node.label, color: col.text, size: '13px' });

    // Type badge
    const typeLabel: Record<string, string> = {
      minor: '◦ Minor', notable: '◈ Notable', elite: '◆ Elite', keystone: '✦ Keystone',
    };
    lines.push({ text: typeLabel[node.type] ?? '', color: '#7a6030', size: '8px' });

    // Branch badge under the type
    const branchBadge: Record<string, string> = {
      war:     'ᚹ WRATH',
      defense: '⛨ WARD',
      fortune: 'ᚠ CUNNING',
    };
    if (branchBadge[node.path]) {
      lines.push({ text: branchBadge[node.path], color: col.text, size: '8px' });
    }

    lines.push({ text: node.desc, color: '#8a7060', size: '9px' });
    lines.push({ text: ' ', color: '#000', size: '4px' });

    const unitMap: Record<string, string> = {
      // legacy stats
      atk: ' ATK', atkSpd: ' atk speed', crit: ' crit', critDmg: ' crit dmg',
      dmgPct: ' damage', mp: ' Mana', cdr: ' cooldown', sp: ' spell power',
      spellDmg: ' spell dmg', goldPct: ' gold', lootPct: ' rune drop',
      resourcePct: ' resources', vendorLocks: ' vendor lock',
      fireRes: ' fire resist', frostRes: ' frost resist',
      shadowRes: ' shadow resist', arcaneRes: ' arcane resist', allRes: ' all resist',
      // WRATH v2
      armorPenPct: ' armour pen', spellCritChance: ' spell crit', spellCritDmg: ' spell crit dmg',
      dmgVsElitePct: ' dmg vs elites', cleavePct: ' cleave', lifestealPct: ' lifesteal',
      dmgPerStagePct: ' dmg per stage', berserkLowHpPct: ' dmg below 30% HP',
      killHealPct: ' HP heal on kill', warmongerPctPerDeadAlly: ' dmg per dead ally',
      bloodfrenzyAtkSpd: ' atk speed on kill (5s)',
      doubleAttackChance: ' chance to double-attack',
      // WARD v2
      hpFlat: ' max HP', hpPctBonus: ' max HP', defFlat: ' defense',
      hpRegenPerSec: ' HP/s regen', hpRegenPctPerSec: ' max HP/s regen',
      healingReceivedPct: ' healing received', bossDrPct: ' damage received from bosses',
      aoeDamageReductionPct: ' AOE damage received',
      drPct: ' damage received', shieldEffPct: ' shield strength',
      ccResistPct: ' CC resist', dotResistPct: ' DoT damage received', dodgePct: ' dodge',
      thornsPct: ' thorns', frontDefPct: ' front-row defense',
      extraRespawns: ' free respawn use', healPerWavePct: ' HP heal per wave',
      lastVowDrPct: ' damage received below 30% HP',
      // CUNNING v2
      xpPct: ' XP', recyclingPct: ' rune refund on scrap',
      chanceToDoubleRecycle: ' chance to double recycle refund',
      spellNoCdChance: ' chance to skip cooldown',
      runeDropPct: ' rune drop',
      bonusMovesPerWave: ' move per wave', extraBookBan: ' book ban slot',
      bookRerolls: ' book reroll', talentRerolls: ' talent reroll',
      shrineDiscountPct: ' altar gold cost', extraBoonSlot: ' boon slot',
      invSlotBonus: ' inventory slot', foresightExtraWaves: ' Foresight wave',
      bookChoiceBonus: ' book choice (per 5 stages)',
      // legacy unique
      hpPenalty: ' max HP penalty', vendorRefreshSpeed: ' merchant refresh', rareLootUpgrade: ' rare→epic upgrade',
      extra_potion_slots: ' potion slot', sigil_slots: ' sigil slot',
    };
    const pctStats = new Set([
      'atkSpd','crit','critDmg','dmgPct','cdr','spellDmg','goldPct','lootPct','resourcePct',
      'fireRes','frostRes','shadowRes','arcaneRes','allRes',
      'armorPenPct','spellCritChance','spellCritDmg','dmgVsElitePct','cleavePct','lifestealPct',
      'dmgPerStagePct','berserkLowHpPct','killHealPct','warmongerPctPerDeadAlly','bloodfrenzyAtkSpd','doubleAttackChance',
      'hpPctBonus','hpRegenPctPerSec','healingReceivedPct','drPct','bossDrPct','aoeDamageReductionPct',
      'shieldEffPct','ccResistPct','dotResistPct','dodgePct',
      'thornsPct','frontDefPct','healPerWavePct','lastVowDrPct',
      'xpPct','recyclingPct','chanceToDoubleRecycle','spellNoCdChance','runeDropPct','shrineDiscountPct','hpPenalty',
    ]);
    // Reduction-style stats — the rank value REDUCES the named effect (damage
    // received, cooldown, shrine cost, etc.). Render with a leading minus so
    // the tooltip reads "−1.0% damage received per rank" instead of the
    // misleading "+1.0% damage received per rank".
    const REDUCTION_STATS = new Set([
      'drPct', 'lastVowDrPct', 'bossDrPct', 'dotResistPct',
      'aoeDamageReductionPct', 'cdr', 'shrineDiscountPct', 'hpPenalty',
    ]);
    for (const eff of node.effects) {
      if (eff.stat.startsWith('keystone')) continue;
      if (eff.stat.endsWith('Flag') || eff.stat === 'deepCheckpoint') continue;  // booleans — desc covers them
      const unit = unitMap[eff.stat] ?? '';
      const isPct = pctStats.has(eff.stat);
      const precision = eff.perRank < 0.01 ? 2 : 1;
      const sign = REDUCTION_STATS.has(eff.stat) ? '−' : '+';
      const v = isPct ? `${sign}${(eff.perRank * 100).toFixed(precision)}%` : `${sign}${eff.perRank}`;
      let totalStr = '';
      if (rank > 0) {
        const total = eff.perRank * rank;
        const tv = isPct ? `${sign}${(total * 100).toFixed(precision)}%` : `${sign}${total}`;
        totalStr = `  (${tv} now)`;
      }
      lines.push({ text: `${v}${unit} per rank${totalStr}`, color: '#c8973a', size: '10px' });
    }

    lines.push({ text: ' ', color: '#000', size: '4px' });
    if (!isMaxed) {
      lines.push({ text: `Rank ${rank} / ${node.maxRank}`, color: '#a07840' });
      const costColor = this.gsm.player.resources.loot >= node.costPerRank ? '#c8973a' : '#6b2020';
      lines.push({ text: `Cost: ${node.costPerRank} ◆ per rank`, color: costColor });
    } else {
      lines.push({ text: 'MAX RANK', color: col.text, size: '11px' });
    }

    // Spend cap warning
    if (atBudget && !isMaxed) {
      lines.push({ text: ' ', color: '#000', size: '4px' });
      lines.push({ text: `✕ Point budget reached (${MAX_TREE_POINTS} max)`, color: '#c05030', size: '9px' });
    }

    // Requirements section
    if (!reqsMet && rank === 0) {
      lines.push({ text: ' ', color: '#000', size: '4px' });
      // Node requirement chain
      const missingNodes: string[] = [];
      for (const reqId of node.requires) {
        const reqNode = getRuneNode(reqId);
        if (!reqNode) continue;
        const reqRank = getNodeRank(tree, reqId);
        if (reqRank < reqNode.maxRank) missingNodes.push(`${reqNode.label} (${reqRank}/${reqNode.maxRank})`);
      }
      if (missingNodes.length > 0) {
        lines.push({ text: 'Requires full rank:', color: '#8b2020', size: '9px' });
        for (const part of missingNodes) lines.push({ text: `  • ${part}`, color: '#7a2818', size: '9px' });
      }
      // Zone gate
      if (node.requiresZone !== undefined) {
        const highest = ctx.highestLevel ?? 0;
        if (highest < node.requiresZone) {
          lines.push({ text: `🗺 Requires Stage ${node.requiresZone} cleared (you: ${highest})`, color: '#8b6020', size: '9px' });
        }
      }
      // Achievement gate
      if (node.requiresAchievement !== undefined) {
        const hasAch = (ctx.completedAchievementIds ?? []).includes(node.requiresAchievement);
        if (!hasAch) {
          lines.push({ text: `🏆 Requires achievement: ${node.requiresAchievement}`, color: '#8b6020', size: '9px' });
        }
      }
      // Branch-rank gate (v2 tier system)
      if (node.requiresBranchRanks !== undefined && node.requiresBranchRanks > 0) {
        const branchSpent = getBranchRanksSpent(tree, node.path);
        if (branchSpent < node.requiresBranchRanks) {
          const branchName = node.path === 'war' ? 'WRATH' : node.path === 'defense' ? 'WARD' : node.path === 'fortune' ? 'CUNNING' : node.path;
          lines.push({
            text: `⛨ Requires ${node.requiresBranchRanks} ${branchName} ranks (you: ${branchSpent})`,
            color: '#8b6020', size: '9px',
          });
        }
      }
    }

    // Measure total tooltip height — account for text wrap
    // Approximate chars per line at 9–10px Georgia serif in a 220px column
    const CHARS_PER_LINE = 36;
    const lineHeight = (size: string | undefined, text: string) => {
      if (size === '4px') return 5;
      const base = size === '13px' ? 18 : size === '11px' ? 16 : 14;
      if (!text.trim()) return base;
      const wrapLines = Math.max(1, Math.ceil(text.length / CHARS_PER_LINE));
      return base * wrapLines;
    };
    const totalH = lines.reduce((sum, l) => sum + lineHeight(l.size, l.text), 0) + 24;

    // Project the node's data-coord into actual scene coords via the tree's zoom + pan transform,
    // then anchor the tooltip to the right side of the rendered node (or the left side if that
    // would clip off-screen). Previously used raw node.x/y which ignored zoom & pan, so the
    // tooltip drifted to a wrong spot after any interaction.
    const r = (NODE_R[node.type] ?? 20) * this.treeZoom;
    const nodeSceneX = this.scrollContainer.x + node.x * this.treeZoom;
    const nodeSceneY = this.scrollContainer.y + node.y * this.treeZoom;
    // Prefer right side; if it would clip, flip to left side of the node.
    const rightX = nodeSceneX + r + 6;
    const leftX  = nodeSceneX - r - 6 - W;
    const sx = rightX + W + 4 > 960 ? leftX : rightX;
    const sy = nodeSceneY - totalH / 2;
    const tx = Phaser.Math.Clamp(sx, 10, 960 - W - 10);
    const ty = Phaser.Math.Clamp(sy, HEADER_H + 4, 576 - totalH - 4);

    const bg = this.add.rectangle(tx + W / 2, ty + totalH / 2, W, totalH, 0x060402, 0.97)
      .setStrokeStyle(1, col.node, 0.8).setDepth(50);
    this.tooltip.add(bg);
    this.tooltip.setPosition(0, 0);

    let curY = ty + 10;
    for (const l of lines) {
      const fSize = l.size ?? '10px';
      const lh = lineHeight(fSize, l.text);
      if (l.text.trim()) {
        const t = this.add.text(tx + 10, curY, l.text, {
          fontSize: fSize, fontFamily: 'Georgia, serif', color: l.color,
          wordWrap: { width: W - 20 },
        }).setDepth(51);
        this.tooltip.add(t);
      }
      curY += lh;
    }

    this.tooltip.setVisible(true);
  }

  private doScroll(delta: number): void {
    this.scrollY = Phaser.Math.Clamp(this.scrollY + delta, 0, this.maxScroll);
    if (this.tooltipHideTimer) { this.tooltipHideTimer.remove(false); this.tooltipHideTimer = null; }
    this.tooltip.setVisible(false);
    this.tooltipNodeId = null;
    this.applyScroll();
  }

  /** Zoom the tree container around its center (clamped 0.5..1.6).
   *  Starts at 0.55 so the full radial tree (ring 4 radius ≈ 510 px, diameter ≈ 1020)
   *  fits the ~504 px usable viewport on first load — no wheel-zoom needed to see it. */
  private treeZoom = 1.0;
  private doZoom(delta: number): void {
    const newZoom = Phaser.Math.Clamp(this.treeZoom + delta, 0.5, 1.6);
    if (newZoom === this.treeZoom) return;
    this.treeZoom = newZoom;
    // Anchor scale around the hub so zooming stays centered on the tree
    const anchorX = this.hubX;
    const anchorY = this.hubY;
    this.scrollContainer.setScale(this.treeZoom);
    // Recompute pivot: origin is (0,0) in container; shift container so hub stays at viewport center
    const viewCenterX = 480;
    const viewCenterY = HEADER_H + (576 - HEADER_H) / 2;
    this.scrollContainer.x = viewCenterX - anchorX * this.treeZoom;
    this.scrollContainer.y = viewCenterY - anchorY * this.treeZoom;
    if (this.tooltipHideTimer) { this.tooltipHideTimer.remove(false); this.tooltipHideTimer = null; }
    this.tooltip.setVisible(false);
    this.tooltipNodeId = null;
  }

  private applyScroll(): void {
    const SCROLL_H = 576 - HEADER_H;
    this.scrollContainer.y = HEADER_H - this.scrollY;
    if (this.scrollBarThumb && this.maxScroll > 0) {
      const pct = this.scrollY / this.maxScroll;
      const trackRange = SCROLL_H - this.scrollBarThumb.height;
      this.scrollBarThumb.y = HEADER_H + pct * trackRange;
    }
    if (this.isDragging) {
      this.tooltip.setVisible(false);
      this.tooltipNodeId = null;
    }
  }
}
